<script
	lang="ts"
	module
>
	import type { Model, InventoryType, Manufacturer, SearchField } from 'utility/search-fields'
	export type Type = 'ASSEMBLY' | 'PART' | 'VEHICLE'

	//end of module section
</script>

<script lang="ts">
	import type { i18n } from 'types/common'

	import Autocomplete from '@isoftdata/svelte-autocomplete'
	import { startsWithVowel } from 'utility/language-tools'
	import { loadPartModelsForInventoryTypeAndManufacturer } from 'utility/search-fields'
	import { untrack, type ComponentProps } from 'svelte'
	import { getContext } from 'svelte'

	interface Props extends Omit<
		ComponentProps<typeof Autocomplete<Model | null>>,
		'value' | 'options' | 'getLabel' | 'type'
	> {
		model?: Model | null
		inventoryType?: InventoryType | null
		manufacturer: Manufacturer | null
		initialValue?: number
		modelList?: Promise<Array<Model>> | Array<Model>
		type: Type // no default, require that they pass it
		disabled?: boolean
	}

	interface Label {
		label: string
		labelPlaceholder: string
		parentLabel: string
	}

	let {
		model = $bindable(null),
		inventoryType = null,
		manufacturer,
		modelList = $bindable([]),
		type,
		disabled = false,
		initialValue,
		...rest
	}: Props = $props()

	const { t: translate } = getContext<i18n>('i18next')

	const typeLabelMap = new Map<Type, Label>([
		[
			'ASSEMBLY',
			{
				label: translate('common:assemblyModel', 'Assembly Model'),
				labelPlaceholder: translate('search.selectAnAssemblyModel', 'Select an Assembly Model'),
				parentLabel: translate(
					'search.selectAnAssemblyManufacturerToShortenThisList',
					'Select an Assembly Manufacturer to shorten this list',
				),
			},
		],
		[
			'PART',
			{
				label: translate('common:partModel', 'Part Model'),
				labelPlaceholder: translate('search.selectAPartModel', 'Select a Part Model'),
				parentLabel: translate(
					'search.selectAPartManufacturerToShortenThisList',
					'Select a Part Manufacturer to shorten this list',
				),
			},
		],
		[
			'VEHICLE',
			{
				label: translate('common:vehicleModel', 'Vehicle Model'),
				labelPlaceholder: translate('search.selectAVehicleModel', 'Select a Vehicle Model'),
				parentLabel: translate(
					'search.selectAVehicleMakeToShortenThisList',
					'Select a Vehicle Make to shorten this list',
				),
			},
		],
	])

	async function loadModels({
		inventoryType,
		manufacturer,
	}: {
		inventoryType: InventoryType | null
		manufacturer: Manufacturer | null
	}) {
		if (inventoryType || manufacturer) {
			modelList = loadPartModelsForInventoryTypeAndManufacturer({
				inventoryTypeId: type === 'ASSEMBLY' ? (inventoryType?.setId ?? inventoryType?.id) : inventoryType?.id,
				manufacturerId: manufacturer?.id,
			})
			modelList.then(loadedModels => {
				if (initialValue) {
					model = loadedModels.find(m => m.id === initialValue) ?? null
				}
				//If we have a selected model but after loading the new list, it's not in the list, then we need to the clear the selection
				else if (model && !loadedModels.find(loadedModel => loadedModel.id === model?.id)) {
					model = null
				}
			})
		} else {
			modelList = []
			model = null
		}
	}

	function computePlaceholder(type: Type, inventoryType: InventoryType | null, manufacturer: Manufacturer | null) {
		const typeLabel = typeLabelMap.get(type)
		if (inventoryType && !manufacturer) {
			return typeLabel?.parentLabel ?? translate('common:select', 'Select')
		} else if (!inventoryType && type !== 'VEHICLE') {
			return translate('search.selectAnInventoryTypeFirst', 'Select an inventory type first')
		} else {
			return typeLabel?.labelPlaceholder ?? translate('common:select', 'Select')
		}
	}

	let placeholder = $derived(computePlaceholder(type, inventoryType, manufacturer))
	let label = $derived(typeLabelMap.get(type)?.label ?? translate('common:model', 'Model'))

	$effect(() => {
		inventoryType
		manufacturer
		untrack(() => loadModels({ inventoryType, manufacturer }))
	})
</script>

<Autocomplete
	{...rest}
	{label}
	{placeholder}
	options={modelList}
	emptyValue={null}
	disabled={disabled || !(inventoryType || manufacturer)}
	bind:value={model}
	getLabel={option => option?.name ?? ''}
></Autocomplete>
